import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SafeVideoPlayerProps {
  videoUrl: string; // For direct files this should be signed URL, for providers the original URL
  className?: string;
  title?: string;
  onProgress?: (watchTime: number, duration: number) => void; // called periodically (throttled)
  onReady?: () => void;
  onError?: (err: unknown) => void;
}

function isYouTubeURL(url: string): boolean {
  return /youtube\.com|youtu\.be/.test(url);
}
function isVimeoURL(url: string): boolean {
  return /vimeo\.com/.test(url);
}
function extractYouTubeID(url: string): string {
  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([^\s&?]+)/);
  return match?.[1] ?? '';
}
function extractVimeoID(url: string): string {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match?.[1] ?? '';
}
function isDirectVideoFile(url: string): boolean {
  return /\.(mp4|webm|mov|ogg|mkv)(\?.*)?$/i.test(url);
}

const SafeVideoPlayer: React.FC<SafeVideoPlayerProps> = ({
  videoUrl,
  className = 'w-full aspect-video',
  title = 'Video Content',
  onProgress,
  onReady,
  onError
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cover to visually mask YouTube branding when not playing and for a brief moment after playback starts
  const [coverVisible, setCoverVisible] = useState(true);
  // Extra slim top mask to hide channel name flash right after playback begins
  const [introTopMask, setIntroTopMask] = useState(false);
  // We default to the highest available quality for YouTube/Vimeo without exposing UI selectors

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressLastSentRef = useRef<number>(0);
  const throttleIntervalSec = 15; // seconds
  const coverCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // YouTube quality control state
  const [qualityLevels, setQualityLevels] = useState<string[]>([]);
  const [selectedQuality, setSelectedQuality] = useState<string | null>(null);

  const pickHighestQuality = useCallback((levels: string[]) => {
    const order = ['highres', 'hd2880', 'hd2160', 'hd1440', 'hd1080', 'hd720', 'large', 'medium', 'small', 'tiny', 'auto'];
    for (const q of order) if (levels.includes(q)) return q;
    return levels[0] || 'auto';
  }, []);

  const qualityLabel = (q: string) => {
    const map: Record<string, string> = {
      highres: 'Best',
      hd2880: '2880p',
      hd2160: '2160p',
      hd1440: '1440p',
      hd1080: '1080p',
      hd720: '720p',
      large: '480p',
      medium: '360p',
      small: '240p',
      tiny: '144p',
      auto: 'Auto'
    };
    return map[q] || q;
  };

  // Progress dispatch throttle, will call onProgress every throttleInterval seconds
  const reportProgressIfNeeded = useCallback((watch: number, total: number) => {
    if (!onProgress) return;
    const nowSec = Math.floor(performance.now() / 1000);
    if (!progressLastSentRef.current || nowSec - progressLastSentRef.current >= throttleIntervalSec) {
      progressLastSentRef.current = nowSec;
      onProgress(Math.floor(watch || 0), Math.floor(total || 0));
    }
  }, [onProgress]);

  // YouTube API initialization
  useEffect(() => {
    if (!isYouTubeURL(videoUrl)) return;
    const videoId = extractYouTubeID(videoUrl);
    if (!videoId) {
      setError(t('video.status.invalidYoutube'));
      onError?.(new Error(t('video.status.invalidYoutube')));
      return;
    }

    // Load YT iframe api if needed
    let apiWasLoadedByUs = false;
    if (!(window as any).YT) {
      apiWasLoadedByUs = true;
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.body.appendChild(script);
      (window as any).onYouTubeIframeAPIReady = () => {
        setIsReady(true);
      };
    } else {
      setIsReady(true);
    }

    const initWhenReady = () => {
      const id = `youtube-player-${videoId}`;
      // Destroy existing if present
      if ((window as any).players && (window as any).players[videoId]) {
        (window as any).players[videoId].destroy();
        delete (window as any).players[videoId];
      }
      try {
        (window as any).players = (window as any).players || {};
        (window as any).players[videoId] = new (window as any).YT.Player(id, {
          height: '100%',
          width: '100%',
          videoId,
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            enablejsapi: 1,
            fs: 0,
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin
          },
          events: {
            onReady: (e: { target: unknown }) => {
              playerRef.current = e.target;
              try {
                const total = playerRef.current.getDuration() || 0;
                setDuration(Math.floor(total));
                // Initialize available quality levels and prefer highest
                try {
                  const levels: string[] = playerRef.current.getAvailableQualityLevels?.() || [];
                  setQualityLevels(levels);
                  const preferred = pickHighestQuality(levels);
                  setSelectedQuality(preferred);
                  playerRef.current.setPlaybackQuality(preferred);
                } catch { }
              } catch (err) { }
              onReady?.();
              setIsReady(true);
              // Show cover initially until playback actually progresses
              setCoverVisible(true);
            },
            // no-op for quality change; we do not show UI
            onStateChange: (e: { data: number }) => {
              const state = e.data;
              setIsPlaying(state === 1);
              if (state === 1) {
                // playing
                // Re-assert highest available quality when playback starts
                try {
                  const levels: string[] = playerRef.current.getAvailableQualityLevels?.() || [];
                  if (levels.length) setQualityLevels(levels);
                  const preferred = selectedQuality || pickHighestQuality(levels);
                  playerRef.current.setPlaybackQuality(preferred);
                } catch { }
                // Keep cover until we see time progress to avoid initial channel-name flash
                setCoverVisible(true);
                if (coverCheckIntervalRef.current) clearInterval(coverCheckIntervalRef.current);
                coverCheckIntervalRef.current = setInterval(() => {
                  try {
                    const t = playerRef.current?.getCurrentTime?.() || 0;
                    if (t > 0.3) {
                      setCoverVisible(false);
                      // show a slim top mask for a short time to hide channel/title text
                      setIntroTopMask(true);
                      setTimeout(() => setIntroTopMask(false), 4000);
                      if (coverCheckIntervalRef.current) {
                        clearInterval(coverCheckIntervalRef.current);
                        coverCheckIntervalRef.current = null;
                      }
                    }
                  } catch { }
                }, 100);

                const interval = setInterval(() => {
                  if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
                    const current = playerRef.current.getCurrentTime() || 0;
                    setCurrentTime(current);
                    reportProgressIfNeeded(current, playerRef.current.getDuration() || 0);
                  }
                }, 1000);
                (playerRef.current as any)._reportInterval = interval;
              } else {
                if (playerRef.current && (playerRef.current as any)._reportInterval) {
                  clearInterval((playerRef.current as any)._reportInterval);
                  (playerRef.current as any)._reportInterval = null;
                }
                if (coverCheckIntervalRef.current) {
                  clearInterval(coverCheckIntervalRef.current);
                  coverCheckIntervalRef.current = null;
                }
                // If paused or ended, still report a final snapshot
                try {
                  const current = playerRef.current.getCurrentTime() || 0;
                  reportProgressIfNeeded(current, playerRef.current.getDuration() || 0);
                } catch (err) { }
                // Show cover again on pause/ended/buffering states
                setCoverVisible(true);
              }
            },
            onError: (e: unknown) => {
              setError(t('video.status.playbackError'));
              onError?.(e);
            }
          }
        });
      } catch (err) {
        setError(t('video.status.youtubeInitFailed'));
        onError?.(err);
      }
    };

    // small delay in case DOM element not mounted yet
    const readyCheck = setInterval(() => {
      if ((window as any).YT && (window as any).YT.Player && document.getElementById(`youtube-player-${videoId}`)) {
        clearInterval(readyCheck);
        initWhenReady();
      }
    }, 200);

    return () => {
      clearInterval(readyCheck);
      try {
        if ((window as any).players && (window as any).players[videoId]) {
          if ((window as any).players[videoId].destroy) (window as any).players[videoId].destroy();
          delete (window as any).players[videoId];
        }
      } catch (err) { }
      if (apiWasLoadedByUs) {
        // we do not remove script tag for safety, but could if desired
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, onReady, onError, reportProgressIfNeeded]);

  // Track video viewing for analytics
  useEffect(() => {
    if (!playerRef.current) return;

    const interval = setInterval(() => {
      if (playerRef.current && isPlaying) {
        const current = playerRef.current.getCurrentTime();
        const total = playerRef.current.getDuration();
        setCurrentTime(current);
        setDuration(Math.floor(total));
        reportProgressIfNeeded(current, total);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, reportProgressIfNeeded]);

  // Native <video> handlers for direct files
  useEffect(() => {
    if (!isDirectVideoFile(videoUrl)) return;
    const v = videoRef.current;
    if (!v) return;

    const timeHandler = () => {
      const cur = v.currentTime || 0;
      const tot = v.duration || 0;
      setCurrentTime(cur);
      if (!duration && tot) setDuration(Math.floor(tot));
      reportProgressIfNeeded(cur, tot);
    };
    const playHandler = () => setIsPlaying(true);
    const pauseHandler = () => setIsPlaying(false);
    const loadedHandler = () => {
      setDuration(Math.floor(v.duration || 0));
      setIsReady(true);
      onReady?.();
    };
    const errorHandler = (e: Event) => {
      setError(t('video.status.videoError'));
      onError?.(e);
    };

    v.addEventListener('timeupdate', timeHandler);
    v.addEventListener('play', playHandler);
    v.addEventListener('pause', pauseHandler);
    v.addEventListener('loadedmetadata', loadedHandler);
    v.addEventListener('error', errorHandler);

    return () => {
      v.removeEventListener('timeupdate', timeHandler);
      v.removeEventListener('play', playHandler);
      v.removeEventListener('pause', pauseHandler);
      v.removeEventListener('loadedmetadata', loadedHandler);
      v.removeEventListener('error', errorHandler);
    };
  }, [videoUrl, duration, onReady, onError, reportProgressIfNeeded]);

  // Simple controls for all variants
  const togglePlay = () => {
    if (isYouTubeURL(videoUrl)) {
      if (playerRef.current) {
        if (isPlaying) playerRef.current.pauseVideo();
        else playerRef.current.playVideo();
      }
    } else if (isVimeoURL(videoUrl)) {
      // Vimeo integration placeholder, implement using postMessage or @vimeo/player
    } else {
      const v = videoRef.current;
      if (!v) return;
      if (isPlaying) v.pause();
      else v.play().catch((err) => {
        setError(t('video.status.playbackBlocked'));
        onError?.(err);
      });
    }
  };

  const toggleMute = () => {
    if (isYouTubeURL(videoUrl) && playerRef.current) {
      if (isMuted) {
        playerRef.current.unMute();
        playerRef.current.setVolume(volume * 100);
      } else {
        playerRef.current.mute();
      }
      setIsMuted(!isMuted);
    } else {
      const v = videoRef.current;
      if (!v) return;
      v.muted = !v.muted;
      setIsMuted(v.muted);
    }
  };

  const handleVolumeChange = (val: number) => {
    setVolume(val);
    if (isYouTubeURL(videoUrl) && playerRef.current) {
      playerRef.current.setVolume(val * 100);
      setIsMuted(val === 0);
    } else {
      const v = videoRef.current;
      if (!v) return;
      v.volume = val;
      setIsMuted(val === 0);
    }
  };

  // Seek helper for both providers
  const seekBy = (deltaSeconds: number) => {
    if (isYouTubeURL(videoUrl) && playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
      const cur = playerRef.current.getCurrentTime() || 0;
      const next = Math.max(0, cur + deltaSeconds);
      playerRef.current.seekTo(next, true);
      setCurrentTime(next);
    } else {
      const v = videoRef.current;
      if (!v) return;
      const next = Math.max(0, (v.currentTime || 0) + deltaSeconds);
      v.currentTime = next;
      setCurrentTime(next);
    }
  };

  // Keyboard controls (focus the container and use arrows/space/m/f)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case ' ': // Space: play/pause
        e.preventDefault();
        togglePlay();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        seekBy(-5);
        break;
      case 'ArrowRight':
        e.preventDefault();
        seekBy(10);
        break;
      case 'ArrowUp':
        e.preventDefault();
        handleVolumeChange(Math.min(1, volume + 0.05));
        break;
      case 'ArrowDown':
        e.preventDefault();
        handleVolumeChange(Math.max(0, volume - 0.05));
        break;
      case 'm':
      case 'M':
        e.preventDefault();
        toggleMute();
        break;
      case 'f':
      case 'F':
        e.preventDefault();
        if (!document.fullscreenElement) containerRef.current?.requestFullscreen();
        else document.exitFullscreen();
        break;
      default:
        break;
    }
  };

  // auto-hide controls timeout
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    if (showControls && isPlaying) {
      t = setTimeout(() => setShowControls(false), 3000);
    }
    return () => {
      if (t) clearTimeout(t);
    };
  }, [showControls, isPlaying]);

  // Show controls on mouse move / touch and when entering fullscreen
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = () => setShowControls(true);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('touchstart', onMove, { passive: true });
    document.addEventListener('fullscreenchange', onMove);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('touchstart', onMove);
      document.removeEventListener('fullscreenchange', onMove);
    };
  }, []);

  // format time
  const formatTime = (s: number) => {
    if (!isFinite(s) || s <= 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // Basic error rendering
  if (!videoUrl) {
    return (
      <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
        <p className="text-gray-500">{t('video.status.noVideo')}</p>
      </div>
    );
  }

  const youTube = isYouTubeURL(videoUrl);
  const vimeo = isVimeoURL(videoUrl);
  const direct = isDirectVideoFile(videoUrl) || videoUrl.startsWith('http');

  if (youTube) {
    const videoId = extractYouTubeID(videoUrl);
    if (!videoId) {
      return (
        <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
          <p className="text-gray-500">{t('video.status.invalidYoutube')}</p>
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        className={`${className} relative bg-black rounded-lg overflow-hidden group`}
        tabIndex={0}
        role="region"
        aria-label={title || 'Video player'}
        onKeyDown={handleKeyDown}
      >
        <div id={`youtube-player-${videoId}`} className="w-full h-full pointer-events-none" />
        {/* Anti-branding cover: visible when not playing, sits above iframe but below interactive overlays */}
        <div
          className={`absolute inset-0 bg-black pointer-events-none z-10 transition-opacity duration-150 ${isPlaying ? 'opacity-0' : 'opacity-100'}`}
          aria-hidden="true"
        />
        {/* overlays and controls */}
        <div className={`absolute inset-0 flex flex-col justify-between transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'} pointer-events-none z-20`}>
          {/* Slim top mask to hide channel name at start */}
          {introTopMask && (
            <div className="absolute top-0 left-0 right-0 h-14 bg-black/90" aria-hidden="true" />
          )}
          {/* Quality selector for YouTube */}
          {qualityLevels.length > 0 && (
            <div className="absolute top-2 left-2 pointer-events-auto">
              <label className="sr-only" htmlFor="yt-quality">Quality</label>
              <select
                id="yt-quality"
                className="text-xs bg-black/70 text-white rounded px-2 py-1 border border-white/20"
                value={selectedQuality || ''}
                onChange={(e) => {
                  const q = e.target.value;
                  setSelectedQuality(q);
                  try { playerRef.current?.setPlaybackQuality?.(q); } catch { }
                }}
              >
                {(qualityLevels || []).map(q => (
                  <option key={q} value={q}>{qualityLabel(q)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="bg-gradient-to-b from-black/50 to-transparent h-16 flex items-start justify-end p-4">
            <div className="bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">{t('video.status.protected')}</div>
          </div>

          {/* Our own bottom-left play button for paused state */}
          {!isPlaying && (
            <div className="absolute bottom-3 left-3 pointer-events-auto">
              <button
                onClick={togglePlay}
                className="bg-black/70 text-white rounded-full p-2 border border-white/20 hover:bg-black/80 transition"
                aria-label="Play"
              >
                <Play size={16} />
              </button>
            </div>
          )}

          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <button onClick={togglePlay} className="bg-primary/90 text-white rounded-full p-4 transition-all duration-200 hover:scale-110 pointer-events-auto">
                <Play size={32} />
              </button>
            </div>
          )}

          <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 space-y-2 pointer-events-auto">
            <div className="flex items-center space-x-2 text-white text-sm">
              <span className="min-w-[40px]">{formatTime(currentTime)}</span>
              <div className="flex-1 relative">
                <input type="range" min={0} max={duration || 0} value={currentTime} onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (playerRef.current) playerRef.current.seekTo(val, true);
                  setCurrentTime(val);
                }} className="w-full h-1 bg-white/30 rounded-lg appearance-none cursor-pointer" />
              </div>
              <span className="min-w-[40px]">{formatTime(duration)}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button onClick={togglePlay} className="text-white">
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>
                <button onClick={() => {
                  if (!document.fullscreenElement) containerRef.current?.requestFullscreen();
                  else document.exitFullscreen();
                }} className="text-white">
                  <Maximize size={20} />
                </button>

                <div className="flex items-center space-x-2">
                  <button onClick={() => toggleMute()} className="text-white">
                    {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>
                  <input type="range" min={0} max={1} step={0.05} value={isMuted ? 0 : volume} onChange={(e) => handleVolumeChange(parseFloat(e.target.value))} className="w-16 h-1" />
                </div>
              </div>
              {/* No quality selector UI to avoid YouTube branding surfacing */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (vimeo) {
    const vimeoId = extractVimeoID(videoUrl);
    if (!vimeoId) {
      return (
        <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
          <p className="text-gray-500">{t('video.status.invalidVimeo')}</p>
        </div>
      );
    }

    // Basic iframe embed, for production integrate @vimeo/player for events and tracking
    return (
      <div className={className}>
        <iframe
          className="w-full h-full rounded-lg"
          src={`https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&sidedock=0&quality=1080p`}
          title={title}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (direct) {
    return (
      <div
        className={className}
        ref={containerRef}
        tabIndex={0}
        role="region"
        aria-label={title || 'Video player'}
        onKeyDown={handleKeyDown}
      >
        <video
          ref={videoRef}
          controls
          className="w-full h-full rounded-lg object-cover bg-black"
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          preload="metadata"
        >
          <source src={videoUrl} />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  return (
    <div className={`${className} bg-gray-100 rounded-lg flex items-center justify-center`}>
      <p className="text-gray-500">{t('video.status.unsupported')}</p>
    </div>
  );
}

export default SafeVideoPlayer;
